import { Fragment, useEffect, useMemo } from "react"

import { Col, Row } from "antd"
import { useSelector } from "react-redux"

import EmptyComponent from "../../Components/Empty"
import RecoverConfigureBanner from "../../Components/RecoverConfigureBanner"
import Spinner from "../../Components/Spinner"
import { selectAccountStatistics } from "../DisputesPage/redux/disputes.slice"
import { ANALYTICS_MOCKED } from "./api/services"
import ActivityStream from "./components/ActivityStream/ActivityStream"
import AnalyticsFilters from "./components/AnalyticsFilters"
import CardNetworkPanel from "./components/CardNetworkPanel/CardNetworkPanel"
import CategoryTrendsChart from "./components/CategoryTrendsChart/CategoryTrendsChart"
import CountriesPanel from "./components/CountriesPanel/CountriesPanel"
import DisputeFlow from "./components/DisputeFlow/DisputeFlow"
import DistributionDonut from "./components/DistributionDonut/DistributionDonut"
import KpiTabs from "./components/KpiTabs/KpiTabs"
import QueueCards from "./components/QueueCards/QueueCards"
import RepeatDisputersPanel from "./components/RepeatDisputersPanel"
import WinRateMatrix from "./components/WinRateMatrix"
import { useAnalyticsScope } from "./hooks/useAnalyticsScope"
import { ANALYTICS_SECTION } from "./redux/analytics.constants"
import {
  selectAnalyticsFilters,
  selectSectionData,
  selectSectionFetching
} from "./redux/analytics.slice"
import { rangeOfPeriod } from "./utils/records"

import styles from "./analytics.module.less"

// Recover Analytics. Mirrors the Prevent overview's shape: a top-left calendar + Compare
// drives the page, clickable KPI tabs switch the trend chart, and a Row gutter={[32,32]} of
// full-width / 2-up panels follows.
//
// Every panel is a section with its own endpoint, the way Health → Overview works. The page
// owns the scope (window, comparison, case type) and hands each section the fetcher of the
// scope it reads — getCompare where the section shows a comparison, getBase where it only
// needs the window, getQueue for the always-current queues. The section asks with its own
// params and reads its own answer from the slice.
const AnalyticsPage = () => {
  const { getQueue, getBase, getCompare } = useAnalyticsScope()
  const { period } = useSelector(selectAnalyticsFilters)
  const businessAccountId = useSelector((state) => state.auth.userInfo?.current_business_account_id)
  // Account-wide counts the Recover shell loads before any page renders. While the endpoints are
  // mocks the page is outside the shell and waits on neither the load nor the count.
  const accountStatistics = useSelector(selectAccountStatistics)
  const statisticsLoaded = useSelector((state) => state.disputes.list.statisticsLoaded)
  const statisticsReady = ANALYTICS_MOCKED || statisticsLoaded
  const hasDisputes = ANALYTICS_MOCKED || accountStatistics?.all > 0
  const kpis = useSelector(selectSectionData(ANALYTICS_SECTION.KPIS))
  const kpisFetching = useSelector(selectSectionFetching(ANALYTICS_SECTION.KPIS))

  const range = useMemo(() => rangeOfPeriod(period), [period])

  // Disputes exist, but none fall in the selected window: one clear page-level hint (with the
  // calendar above still reachable to widen the range) instead of every panel's own empty.
  // Decided only once kpis/ has answered, so while it loads the panels mount and ask in
  // parallel with it rather than after it.
  const nothingInWindow = !kpisFetching && kpis?.summary?.disputes_count === 0

  useEffect(() => {
    document.title = "Analytics - Chargeback"
  }, [])

  return (
    <div className={styles.page}>
      <RecoverConfigureBanner />
      {!statisticsReady ? (
        <div className={styles.loading}>
          <Spinner />
        </div>
      ) : !hasDisputes ? (
        <EmptyComponent type="no_disputes" />
      ) : (
        <Row gutter={[16, 16]} align="stretch">
          <Col span={24}>
            <AnalyticsFilters />
          </Col>
          {/* A business-account switch is a different page of data, not an update to this one:
              keying the sections on the account remounts them, and every one asks again. */}
          <Fragment key={businessAccountId}>
            <Col span={24}>
              <KpiTabs getData={getCompare} />
            </Col>
            <Col span={24}>
              {/* The Disputes list's own action cards, directly below the main chart. They
                  ignore the calendar, so the counts are an always-current queue. */}
              <QueueCards getData={getQueue} />
            </Col>
            {nothingInWindow ? (
              <Col span={24}>
                <EmptyComponent type="no_disputes_in_range" />
              </Col>
            ) : (
              <>
                <Col span={24}>
                  <DisputeFlow getData={getBase} />
                </Col>
                <Col xs={24} lg={12}>
                  <DistributionDonut getData={getCompare} withBreakdownLink={false} />
                </Col>
                <Col xs={24} lg={12}>
                  <CountriesPanel getData={getCompare} range={range} />
                </Col>
                <Col xs={24} lg={12}>
                  <CardNetworkPanel getData={getBase} />
                </Col>
                <Col xs={24} lg={12}>
                  <WinRateMatrix getData={getBase} />
                </Col>
                {/* Full width: a table that reads better wide than half-width beside a hole. */}
                <Col span={24}>
                  <RepeatDisputersPanel getData={getCompare} />
                </Col>
                <Col span={24}>
                  <CategoryTrendsChart getData={getBase} />
                </Col>
                <Col span={24}>
                  <ActivityStream getData={getBase} />
                </Col>
              </>
            )}
          </Fragment>
        </Row>
      )}
    </div>
  )
}

export default AnalyticsPage
